/**
 * BatteryHub 2026 — Enhanced storefront JS
 * Features: smooth scroll, header, mobile nav, reveal animations,
 *           mobile filters, payment UX feedback, cart interactions
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Smooth scroll ─────────────────────────────────────── */
  document.querySelectorAll('[data-scroll-target]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      var el = document.querySelector(btn.dataset.scrollTarget);
      if (!el) return;
      e.preventDefault();
      el.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    });
  });

  /* ── Sticky header ─────────────────────────────────────── */
  var header = document.querySelector('.bh-header');
  if (header) {
    var updateHeader = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 12);
    };
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });
  }

  /* ── Mobile nav ────────────────────────────────────────── */
  var menuButton = document.querySelector('[data-mobile-menu]');
  var nav = document.getElementById('BhMainNav');

  if (menuButton && nav) {
    menuButton.addEventListener('click', function () {
      var expanded = menuButton.getAttribute('aria-expanded') === 'true';
      menuButton.setAttribute('aria-expanded', String(!expanded));
      nav.classList.toggle('is-open', !expanded);
      document.body.classList.toggle('bh-nav-open', !expanded);
    });

    nav.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () {
        menuButton.setAttribute('aria-expanded', 'false');
        nav.classList.remove('is-open');
        document.body.classList.remove('bh-nav-open');
      });
    });

    document.addEventListener('click', function (e) {
      if (!header || header.contains(e.target)) return;
      menuButton.setAttribute('aria-expanded', 'false');
      nav.classList.remove('is-open');
      document.body.classList.remove('bh-nav-open');
    });
  }

  /* ── Scroll-reveal ─────────────────────────────────────── */
  var revealSelectors = [
    '.bh-section-card',
    '.bh-product-card',
    '.bh-cat-card',
    '.bh-use-card',
    '.bh-trust-row',
    '.bh-cta',
    '[id^="bh-product-"] .bh-gallery-panel',
    '[id^="bh-product-"] .bh-summary-card',
    '[id^="bh-product-"] .bh-detail-panel',
    '[id^="bh-cart-"] .bh-cart-list',
    '[id^="bh-cart-"] .bh-cart-summary'
  ].join(',');

  var revealTargets = document.querySelectorAll(revealSelectors);

  if (revealTargets.length) {
    revealTargets.forEach(function (el, i) {
      el.classList.add('bh-reveal');
      el.style.setProperty('--bh-reveal-delay', (Math.min(i % 6, 5) * 48) + 'ms');
    });

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      revealTargets.forEach(function (el) { el.classList.add('is-visible'); });
    } else {
      var revealObserver = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });

      revealTargets.forEach(function (el) { revealObserver.observe(el); });
    }
  }

  /* ── Sort auto-submit ──────────────────────────────────── */
  document.querySelectorAll('[data-bh-sort]').forEach(function (select) {
    select.addEventListener('change', function () {
      var form = select.closest('form');
      if (form) form.submit();
    });
  });

  /* ── Mobile filter drawer ──────────────────────────────── */
  var filterDrawer = document.querySelector('[data-filter-drawer]');
  var filterOpenButton = document.querySelector('[data-filter-open]');
  var filterCloseButtons = document.querySelectorAll('[data-filter-close]');

  if (filterDrawer && filterOpenButton) {
    var mobileFilters = window.matchMedia('(max-width: 990px)');

    var setFilterOpen = function (open) {
      filterDrawer.classList.toggle('is-open', open);
      document.body.classList.toggle('bh-filter-open', open);
      filterOpenButton.setAttribute('aria-expanded', String(open));
      filterDrawer.setAttribute('aria-hidden', String(mobileFilters.matches && !open));
    };

    setFilterOpen(false);
    filterOpenButton.addEventListener('click', function () { setFilterOpen(true); });
    filterCloseButtons.forEach(function (btn) {
      btn.addEventListener('click', function () { setFilterOpen(false); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') setFilterOpen(false);
    });
    filterDrawer.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () { setFilterOpen(false); });
    });
    mobileFilters.addEventListener('change', function () { setFilterOpen(false); });
  }

  /* ── Payment button UX feedback ────────────────────────── */
  /**
   * Add tactile feedback to Add-to-Cart and Checkout buttons:
   * - Loading spinner during submission
   * - Ripple effect on click
   * - Success flash on add-to-cart
   */

  function addRipple(btn, e) {
    if (prefersReducedMotion) return;
    var rect = btn.getBoundingClientRect();
    var x = (e.clientX - rect.left) + 'px';
    var y = (e.clientY - rect.top)  + 'px';
    var ripple = document.createElement('span');
    ripple.style.cssText = [
      'position:absolute',
      'border-radius:50%',
      'pointer-events:none',
      'width:10px', 'height:10px',
      'left:' + x, 'top:' + y,
      'transform:translate(-50%,-50%) scale(0)',
      'background:rgba(255,255,255,0.36)',
      'animation:bh-ripple 420ms ease-out forwards'
    ].join(';');
    btn.appendChild(ripple);
    setTimeout(function () { ripple.remove(); }, 450);
  }

  // Inject ripple keyframes once
  if (!document.getElementById('bh-ripple-style')) {
    var st = document.createElement('style');
    st.id = 'bh-ripple-style';
    st.textContent = '@keyframes bh-ripple{to{transform:translate(-50%,-50%) scale(28);opacity:0}}';
    document.head.appendChild(st);
  }

  // Add-to-cart buttons
  document.querySelectorAll('[data-bh-add-button]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      addRipple(btn, e);
    });
  });

  // Checkout buttons
  document.querySelectorAll('.bh-checkout-btn, .bh-add-btn').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      addRipple(btn, e);
    });
  });

  /* ── Cart drawer backdrop close ────────────────────────── */
  document.querySelectorAll('[data-bh-cart-drawer]').forEach(function (drawer) {
    drawer.addEventListener('click', function (e) {
      if (e.target === drawer) {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
      }
    });
  });

  /* ── Payment trust icons injection ─────────────────────── */
  /**
   * Injects a "Secure Checkout" trust strip below payment buttons
   * on the product page and cart page if not already present.
   */
  function buildTrustStrip() {
    var html = '<div class="bh-payment-trust">' +
      '<span class="bh-payment-trust-label">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">' +
          '<rect x="3" y="11" width="18" height="11" rx="2"/>' +
          '<path d="M7 11V7a5 5 0 0 1 10 0v4"/>' +
        '</svg>' +
        'Secure Checkout' +
      '</span>' +
      '<div class="bh-payment-icons">' +
        '<span class="bh-pay-icon">VISA</span>' +
        '<span class="bh-pay-icon">MC</span>' +
        '<span class="bh-pay-icon">AMEX</span>' +
        '<span class="bh-pay-icon">PayPal</span>' +
        '<span class="bh-pay-icon">Apple Pay</span>' +
        '<span class="bh-pay-icon">Google Pay</span>' +
      '</div>' +
    '</div>';
    return html;
  }

  // Product page: after .bh-button-stack
  document.querySelectorAll('[id^="bh-product-"] .bh-button-stack').forEach(function (stack) {
    if (!stack.parentNode.querySelector('.bh-payment-trust')) {
      stack.insertAdjacentHTML('afterend', buildTrustStrip());
    }
  });

  // Cart page: after .bh-checkout-form
  document.querySelectorAll('[id^="bh-cart-"] .bh-checkout-form').forEach(function (form) {
    if (!form.parentNode.querySelector('.bh-cart-trust')) {
      var trustHtml = '<div class="bh-cart-trust">' +
        '<div class="bh-cart-trust-row">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>' +
          '<span><strong>SSL Encrypted</strong> — your data is protected</span>' +
        '</div>' +
        '<div class="bh-cart-trust-row">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12V22H4V12"/><path d="M22 7H2v5h20V7z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>' +
          '<span>Free returns · 30-day money-back guarantee</span>' +
        '</div>' +
      '</div>';
      form.insertAdjacentHTML('afterend', trustHtml);
    }
  });

  /* ── "or pay with" divider between buttons ─────────────── */
  document.querySelectorAll('[id^="bh-product-"] .bh-button-stack').forEach(function (stack) {
    var addBtn = stack.querySelector('[data-bh-add-button]');
    var payBtn = stack.querySelector('.shopify-payment-button');
    if (addBtn && payBtn && !stack.querySelector('.bh-pay-divider')) {
      addBtn.insertAdjacentHTML('afterend', '<div class="bh-pay-divider">or</div>');
    }
  });

  /* ── Cart item micro-animation ─────────────────────────── */
  var cartDot = document.querySelector('.bh-cart-dot');

  document.addEventListener('bh:cart:added', function () {
    if (!cartDot || prefersReducedMotion) return;
    cartDot.style.animation = 'none';
    cartDot.offsetHeight; // reflow
    cartDot.style.animation = 'bh-cart-pop 300ms ease-out';
  });

  if (!document.getElementById('bh-cart-pop-style')) {
    var cs = document.createElement('style');
    cs.id = 'bh-cart-pop-style';
    cs.textContent = '@keyframes bh-cart-pop{0%{transform:scale(1)}40%{transform:scale(1.45)}100%{transform:scale(1)}}';
    document.head.appendChild(cs);
  }

})();
